import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Container, 
    Stack, 
    CircularProgress,
    Button,
    Paper,
    alpha,
    useTheme,
    IconButton,
    Avatar,
    AvatarGroup,
    Tooltip
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { 
    AreaChart, 
    Area, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip as RechartsTooltip, 
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell
} from 'recharts';

import StatCard from '../components/crm/StatCard';
import CareRequestList from '../components/crm/CareRequestList';
import AdmissionsPipeline from '../components/crm/AdmissionsPipeline';
import careService from '../services/careService';
import { CareRequestDTO, ProspectiveResidentDTO, CareDashboardStats, CareStatus, CarePriority } from '../types/crm.types';
import { useNavigate } from 'react-router-dom';

const CareCommandCenterPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<CareDashboardStats | null>(null);
    const [requests, setRequests] = useState<CareRequestDTO[]>([]);
    const [prospects, setProspects] = useState<ProspectiveResidentDTO[]>([]);

    const chartData = [
        { name: 'Mon', requests: 4, admissions: 2 },
        { name: 'Tue', requests: 7, admissions: 5 },
        { name: 'Wed', requests: 5, admissions: 8 },
        { name: 'Thu', requests: 9, admissions: 6 },
        { name: 'Fri', requests: 12, admissions: 10 },
        { name: 'Sat', requests: 4, admissions: 4 },
        { name: 'Sun', requests: 3, admissions: 2 },
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsData, requestsData, prospectsData] = await Promise.all([
                    careService.getDashboardStats(),
                    careService.getTickets(),
                    careService.getLeads()
                ]);
                setStats(statsData);
                setRequests(requestsData);
                setProspects(prospectsData);
            } catch (error) {
                console.error('Failed to fetch Care Management data', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh" sx={{ background: theme.palette.background.default }}>
                <CircularProgress thickness={5} size={60} sx={{ color: theme.palette.primary.main }} />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            background: `radial-gradient(circle at 0% 0%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%), radial-gradient(circle at 100% 100%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 50%)`,
            pt: 4,
            pb: 8
        }}>
            <Container maxWidth={false}>
                {/* Header Section */}
                <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" gap={2} sx={{ mb: { xs: 4, md: 6 } }}>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main', animation: 'pulse 2s infinite' }} />
                            <Typography variant="overline" fontWeight="900" color="primary" sx={{ letterSpacing: 2 }}>
                                Live Monitoring
                            </Typography>
                        </Stack>
                        <Typography variant="h3" fontWeight="900" color="text.primary" sx={{ letterSpacing: -1.5, fontSize: { xs: '1.9rem', sm: '2.4rem', md: '3rem' } }}>
                            Care Command Center
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: '500', opacity: 0.8 }}>
                            Real-time insights into resident care requests and admissions funnel.
                        </Typography>
                    </Box>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ mb: 1 }}>
                        <Button 
                            variant="outlined" 
                            startIcon={<AddIcon />} 
                            onClick={() => navigate('/crm/leads')}
                            sx={{ borderRadius: 3, px: 3, py: 1.5, fontWeight: '800', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                        >
                            New Admission
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<AddIcon />} 
                            onClick={() => navigate('/crm/tickets')}
                            sx={{ borderRadius: 3, px: 3, py: 1.5, fontWeight: '800', boxShadow: theme.shadows[4] }}
                        >
                            Care Request
                        </Button>
                    </Stack>
                </Box>

                {/* Stats Grid */}
                {stats && (
                    <Grid container spacing={3} sx={{ mb: 5 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard 
                                title="Active Requests" 
                                value={stats.totalActiveRequests} 
                                icon={<ConfirmationNumberIcon />} 
                                color="#6366f1" 
                                trend={{ value: '8%', isPositive: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard 
                                title="Pending Response" 
                                value={stats.pendingRequests} 
                                icon={<PendingActionsIcon />} 
                                color="#f59e0b" 
                                trend={{ value: '3%', isPositive: false }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard 
                                title="Critical Alerts" 
                                value={stats.criticalCareAlerts} 
                                icon={<WarningAmberIcon />} 
                                color="#ef4444" 
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard 
                                title="Admissions Rate" 
                                value="14.2%" 
                                icon={<TrendingUpIcon />} 
                                color="#10b981" 
                                trend={{ value: '2.1%', isPositive: true }}
                            />
                        </Grid>
                    </Grid>
                )}

                <Grid container spacing={4}>
                    {/* Charts & Analytics */}
                    <Grid item xs={12} lg={8}>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 4, 
                                borderRadius: 5, 
                                border: '1px solid', 
                                borderColor: 'divider',
                                background: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(20px)',
                                height: 400
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                                <Box>
                                    <Typography variant="h6" fontWeight="900">Activity Overview</Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight="600">Care Requests vs Admissions this week</Typography>
                                </Box>
                                <Stack direction="row" spacing={3}>
                                    <Box display="flex" alignItems="center">
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mr: 1 }} />
                                        <Typography variant="caption" fontWeight="700">Requests</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main', mr: 1 }} />
                                        <Typography variant="caption" fontWeight="700">Admissions</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorAdmissions" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.divider, 0.5)} />
                                    <XAxis 
                                        dataKey="name" 
                                        axisLine={false} 
                                        tickLine={false} 
                                        tick={{ fill: theme.palette.text.secondary, fontSize: 12, fontWeight: 700 }} 
                                        dy={10}
                                    />
                                    <YAxis hide />
                                    <RechartsTooltip 
                                        contentStyle={{ 
                                            borderRadius: '12px', 
                                            border: 'none', 
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                            fontWeight: 800
                                        }} 
                                    />
                                    <Area type="monotone" dataKey="requests" stroke={theme.palette.primary.main} strokeWidth={4} fillOpacity={1} fill="url(#colorRequests)" />
                                    <Area type="monotone" dataKey="admissions" stroke={theme.palette.secondary.main} strokeWidth={4} fillOpacity={1} fill="url(#colorAdmissions)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Team Performance */}
                    <Grid item xs={12} lg={4}>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 4, 
                                borderRadius: 5, 
                                border: '1px solid', 
                                borderColor: 'divider',
                                background: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(20px)',
                                height: 400,
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Typography variant="h6" fontWeight="900" sx={{ mb: 1 }}>Care Staff Utilization</Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight="600" sx={{ mb: 4 }}>Resolution efficiency by staff member</Typography>
                            
                            <Stack spacing={3} sx={{ flexGrow: 1 }}>
                                {Object.entries(stats?.staffWorkload || {}).map(([staff, count]) => (
                                    <Box key={staff}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                                                    {staff[0]}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight="800">{staff}</Typography>
                                            </Stack>
                                            <Typography variant="caption" fontWeight="900" color="primary">{count as number} Requests</Typography>
                                        </Box>
                                        <Box sx={{ width: '100%', height: 6, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 3, overflow: 'hidden' }}>
                                            <Box sx={{ width: `${((count as number) / 5) * 100}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 3 }} />
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>

                            <Button 
                                variant="text" 
                                fullWidth 
                                sx={{ mt: 2, fontWeight: 800, borderRadius: 2 }}
                                onClick={() => navigate('/crm/field-service')}
                            >
                                View All Assignments
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Recent Content */}
                    <Grid item xs={12}>
                        <Box sx={{ mt: 4 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                                <Box>
                                    <Typography variant="h5" fontWeight="900">Priority Backlog</Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight="600">Urgent care needs requiring immediate intervention</Typography>
                                </Box>
                                <Button 
                                    variant="outlined"
                                    onClick={() => navigate('/crm/tickets')}
                                    sx={{ borderRadius: 2, fontWeight: 800 }}
                                >
                                    View Full List
                                </Button>
                            </Box>
                            <CareRequestList 
                                tickets={requests.slice(0, 4)} 
                                onView={() => navigate('/care/requests')} 
                                onEdit={() => navigate('/care/requests')} 
                            />
                        </Box>
                    </Grid>

                    {/* Admissions Funnel Highlights */}
                    <Grid item xs={12}>
                        <Box sx={{ mt: 6, mb: 10 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                                <Box>
                                    <Typography variant="h5" fontWeight="900">Admissions Pipeline</Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight="600">Visualizing resident admission progression</Typography>
                                </Box>
                                <Button 
                                    variant="outlined"
                                    onClick={() => navigate('/crm/leads')}
                                    sx={{ borderRadius: 2, fontWeight: 800 }}
                                >
                                    Admissions Dashboard
                                </Button>
                            </Box>
                            <AdmissionsPipeline leads={prospects} />
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            <style>
                {`
                    @keyframes pulse {
                        0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7); }
                        70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(99, 102, 241, 0); }
                        100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
                    }
                `}
            </style>
        </Box>
    );
};

export default CareCommandCenterPage;
